import { useState, useEffect, useCallback } from 'react';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { coffeeClusterPaths } from '../../coffee_card_web/api/paths';
import { getAuthToken } from '../../../utils/authTokens';

export interface Cluster {
    _id: string;
    name: string;
    slug: string;
    h3Cells: string[];
    h3Resolution: number;
    boundary?: { type: string; coordinates: number[][][] };
    center?: { lat: number; lng: number };
    city: string;
    country: string;
    status: string;
    totalShops: number;
    splitThreshold: number;
    needsSplit?: boolean;
    lastFetchedAt?: string;
    nextRefreshAfter?: string;
    fetchHistory?: Array<{ date: string; shopsFound: number; shopsAdded: number; shopsClosed: number }>;
    createdBy?: { username?: string };
}

export interface GridCell {
    cell: string;
    center: { lat: number; lng: number };
    clusterId: string | null;
    clusterName: string | null;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export function useClusters() {
    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchClusters = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiClient.get(`${url}${coffeeClusterPaths.list()}`, authHeaders());
            setClusters(res.data?.clusters || []);
        } catch (err: any) {
            const raw = err?.response?.data?.error;
            const msg = typeof raw === 'object' && raw?.message ? raw.message : (typeof raw === 'string' ? raw : err?.response?.data?.message || 'Failed to fetch clusters');
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClusters();
    }, [fetchClusters]);

    return { clusters, loading, error, refetch: fetchClusters };
}

export function useCluster(id: string | null) {
    const [cluster, setCluster] = useState<Cluster | null>(null);
    const [loading, setLoading] = useState(!!id);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) {
            setCluster(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        apiClient.get(`${url}${coffeeClusterPaths.byId(id)}`, authHeaders())
            .then(res => setCluster(res.data?.cluster || null))
            .catch(err => setError(err?.response?.data?.error || 'Failed to fetch cluster'))
            .finally(() => setLoading(false));
    }, [id]);

    return { cluster, loading, error };
}

export function useClusterBoundaries() {
    const [boundaries, setBoundaries] = useState<Cluster[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiClient.get(`${url}${coffeeClusterPaths.boundaries()}`, authHeaders())
            .then(res => setBoundaries(res.data?.clusters || []))
            .catch(() => setBoundaries([]))
            .finally(() => setLoading(false));
    }, []);

    return { boundaries, loading };
}

export function useH3Grid(lat: number, lng: number, radius = 5000) {
    const [grid, setGrid] = useState<GridCell[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchGrid = useCallback(async () => {
        if (isNaN(lat) || isNaN(lng)) return;
        setLoading(true);
        try {
            const res = await apiClient.get(`${url}${coffeeClusterPaths.grid()}`, {
                ...authHeaders(),
                params: { lat, lng, radius }
            });
            setGrid(res.data?.grid || []);
        } catch {
            setGrid([]);
        } finally {
            setLoading(false);
        }
    }, [lat, lng, radius]);

    useEffect(() => {
        fetchGrid();
    }, [fetchGrid]);

    return { grid, loading, refetch: fetchGrid };
}

export function useUnclaimedNeighbors(clusterId?: string | null) {
    const [grid, setGrid] = useState<GridCell[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchUnclaimed = useCallback(async () => {
        if (!clusterId) {
            setGrid([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await apiClient.get(`${url}${coffeeClusterPaths.unclaimedNeighbors()}`, {
                ...authHeaders(),
                params: { clusterId }
            });
            setGrid(res.data?.grid || []);
        } catch {
            setGrid([]);
        } finally {
            setLoading(false);
        }
    }, [clusterId]);

    useEffect(() => {
        fetchUnclaimed();
    }, [fetchUnclaimed]);

    return { grid, loading, refetch: fetchUnclaimed };
}

export function useClusterMutations(refetch: () => void) {
    const create = useCallback(async (body: { name: string; h3Cells: string[]; city: string; country: string }) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.create()}`, body, authHeaders());
        refetch();
        return res.data?.cluster;
    }, [refetch]);

    const update = useCallback(async (id: string, body: Partial<{ name: string; h3Cells: string[]; city: string; country: string }>) => {
        const res = await apiClient.put(`${url}${coffeeClusterPaths.byId(id)}`, body, authHeaders());
        refetch();
        return res.data?.cluster;
    }, [refetch]);

    const remove = useCallback(async (id: string) => {
        await apiClient.delete(`${url}${coffeeClusterPaths.byId(id)}`, authHeaders());
        refetch();
    }, [refetch]);

    const fetchShops = useCallback(async (id: string) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.fetch(id)}`, {}, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    const refresh = useCallback(async (id: string) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.refresh(id)}`, {}, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    const suggestSplit = useCallback(async (id: string) => {
        const res = await apiClient.get(`${url}${coffeeClusterPaths.suggestSplit(id)}`, authHeaders());
        return res.data;
    }, []);

    const split = useCallback(async (id: string, body: { clusterA: { name: string; h3Cells: string[] }; clusterB: { name: string; h3Cells: string[] } }) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.split(id)}`, body, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    const fetchEstimate = useCallback(async (id: string) => {
        const res = await apiClient.get(`${url}${coffeeClusterPaths.fetchEstimate(id)}`, authHeaders());
        return res.data;
    }, []);

    const searchPlaces = useCallback(async (id: string, query: string) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.search(id)}`, { query }, authHeaders());
        return res.data;
    }, []);

    const addPlaces = useCallback(async (id: string, places: Array<{ place_id: string; name: string; lat: number; lng: number; address?: string; types?: string[] }>) => {
        const res = await apiClient.post(`${url}${coffeeClusterPaths.addPlaces(id)}`, { places }, authHeaders());
        refetch();
        return res.data;
    }, [refetch]);

    return { create, update, remove, fetchShops, refresh, suggestSplit, split, fetchEstimate, searchPlaces, addPlaces };
}
